/**
 * Servicio para limpiar y migrar datos antiguos de IndexedDB
 */
import { Injectable, inject } from '@angular/core';
import { openDB } from 'idb';
import { LogService } from './log.service';

export interface LegacyCheckIn {
  id: string;
  createdAt: number;
  location: {
    latitude: number;
    longitude: number;
    precision: number;
  };
}

export interface LegacyDeviceInfo {
  deviceType: 'mobile' | 'tablet' | 'desktop';
  os: string;
  version: string;
  mobile: boolean;
  resolution: string;
  networkType: string;
  timeHour: number;
  timezone: string;
}

export interface LegacyCheckInData {
  id: string;
  ubicacion: {
    latitud: number;
    longitud: number;
    precision: number;
    timestamp: string;
  };
  sessionId: string;
  fingerprintId: string;
  deviceInfo: LegacyDeviceInfo;
  createdAt: number;
  status: 'pending' | 'syncing' | 'failed' | 'synced';
  lastAttemptAt?: number;
  error?: string;
}

export interface LegacyPendingRemito {
  id: string;
  qrCode: string;
  imageKey: string;
  image: string;
  createdAt: number;
  sessionId: string;
  checkIn: {
    id: string;
    createdAt: string;
    location: {
      latitude: number;
      longitude: number;
      precision: number;
    };
  };
  observation: string;
  syncAttempts: number;
  status: 'pending' | 'syncing' | 'failed' | 'synced';
  error?: string;
  lastAttemptAt?: number;
}

@Injectable({ providedIn: 'root' })
export class MigrationService {
  private readonly logger = inject(LogService);
  private readonly DB_NAME = 'remitos-pwa';
  private readonly LOCATION_DB_NAME = 'remitos-pwa-location';
  private readonly STORE_NAME = 'pending-remitos';
  private readonly LOCATION_STORE_NAME = 'pending-locations';
  private readonly LOCATION_DB_VERSION = 1;
  private readonly REMITO_DB_VERSION = 5;

  async cleanLegacyData(): Promise<void> {
    try {
      this.logger.info('[MigrationService] Iniciando limpieza de datos antiguos');

      await this.cleanRemitoDb();
      await this.cleanLocationDb();

      this.logger.info('[MigrationService] Limpieza de datos antiguos completada');
    } catch (error) {
      this.logger.error('[MigrationService] Error al limpiar datos antiguos', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  private async cleanRemitoDb(): Promise<void> {
    try {
      const db = await openDB(this.DB_NAME, this.REMITO_DB_VERSION);

      this.logger.info('[MigrationService] Limpiando base de datos de remitos');

      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const records = await tx.store.getAll();

      let cleanedCount = 0;
      let invalidCount = 0;

      for (const record of records) {
        const isLegacy = this.isLegacyRemito(record);

        if (isLegacy) {
          this.logger.debug('[MigrationService] Eliminando remito antiguo', { id: record.id });
          await tx.store.delete(record.id);
          invalidCount++;
        } else {
          cleanedCount++;
        }
      }

      await tx.done;

      this.logger.info('[MigrationService] Limpieza de remitos completada', {
        invalidCount,
        cleanedCount
      });
    } catch (error) {
      this.logger.error('[MigrationService] Error al limpiar base de datos de remitos', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  private async cleanLocationDb(): Promise<void> {
    try {
      const db = await openDB(this.LOCATION_DB_NAME, this.LOCATION_DB_VERSION);

      this.logger.info('[MigrationService] Limpiando base de datos de ubicaciones');

      const tx = db.transaction(this.LOCATION_STORE_NAME, 'readwrite');
      const records = await tx.store.getAll();

      let cleanedCount = 0;
      let invalidCount = 0;

      for (const record of records) {
        const isLegacy = this.isLegacyLocation(record);

        if (isLegacy) {
          this.logger.debug('[MigrationService] Eliminando ubicación antigua', { id: record.id });
          await tx.store.delete(record.id);
          invalidCount++;
        } else {
          cleanedCount++;
        }
      }

      await tx.done;

      this.logger.info('[MigrationService] Limpieza de ubicaciones completada', {
        invalidCount,
        cleanedCount
      });
    } catch (error) {
      this.logger.error('[MigrationService] Error al limpiar base de datos de ubicaciones', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  private isLegacyRemito(record: unknown): boolean {
    if (!record || typeof record !== 'object') return false;

    const typedRecord = record as {
      id?: string;
      checkIn?: {
        deviceInfo?: {
          fingerprintId?: string;
        };
      };
    };

    // Verificar si tiene el formato antiguo del check-in (sin deviceInfo y fingerprintId)
    if (!typedRecord.checkIn) return false;

    const checkIn = typedRecord.checkIn;

    if (!checkIn.deviceInfo || !checkIn.deviceInfo.fingerprintId) {
      this.logger.debug('[MigrationService] Detectado remito antiguo sin deviceInfo/fingerprintId', {
        id: typedRecord.id
      });
      return true;
    }

    return false;
  }

  private isLegacyLocation(record: unknown): boolean {
    if (!record || typeof record !== 'object') return false;

    const typedRecord = record as {
      ubicacion?: {
        timestamp?: string;
      };
      sessionId?: string;
    };

    // Verificar si tiene el formato antiguo
    if (!typedRecord.ubicacion || !typedRecord.sessionId) return false;

    const ubicacion = typedRecord.ubicacion;

    if (!ubicacion.timestamp) return true;

    return false;
  }

  async getCleanStatus(): Promise<{ remitos: number; ubicaciones: number }> {
    try {
      const [remitosCount, ubicacionesCount] = await Promise.all([
        this.getRemitosCount(),
        this.getUbicacionesCount()
      ]);

      return { remitos: remitosCount, ubicaciones: ubicacionesCount };
    } catch (error) {
      this.logger.error('[MigrationService] Error al obtener estado', {
        error: error instanceof Error ? error.message : String(error)
      });
      return { remitos: 0, ubicaciones: 0 };
    }
  }

  private async getRemitosCount(): Promise<number> {
    const db = await openDB(this.DB_NAME, this.REMITO_DB_VERSION);
    const tx = db.transaction(this.STORE_NAME, 'readonly');
    return tx.store.count();
  }

  private async getUbicacionesCount(): Promise<number> {
    const db = await openDB(this.LOCATION_DB_NAME, this.LOCATION_DB_VERSION);
    const tx = db.transaction(this.LOCATION_STORE_NAME, 'readonly');
    return tx.store.count();
  }
}
